import { ofType } from 'redux-observable';
import { of, empty, Observable } from 'rxjs';
import { map, mergeMap } from 'rxjs/operators';

import { ActionManagerInstance } from '../../actions/manager';
import constants, { publicConstants } from '../../constants';
import { getResolvedTargetName } from '../../router/routing-manager';
import * as actionType from '../../store/state/actions';
import * as actions from '../../store/state/actions/action-creators';
import * as StateManager from '../../store/state/state-manager';
import {
  addPreviewResetAction,
  getActionBasedOnContainerMode,
  getActionPayload,
  getActivationDetails,
  getContext,
  getDirtyDetails,
  isAddContainerAction,
  processDataToDispatchErrorAction,
  processViewResponse,
  addActionsToParentContext,
  updateTraditionalGadgetId
} from '../../store/state/epics/util';
import { getTranslatedDXAPIResponse } from '../../utils/dxapi-response-translator';
import {
  getContainerName,
  isActivateContainerItemActionType,
  isErrorActionType,
  updateKeyAndFlowName,
  promiseHandler,
  isParentAndChildContextHoldingSameItem,
  getContainerNameFromContainerItemID,
  isCoexistenceCase,
  getCurrentCaseViewMode
} from '../common-helper';
import { publishCaseOpenedEvent } from '../common-events';
import {
  getTargetFromContainerItemID,
  getContainerItemName,
  getContainerItemData,
  isContainerSingleMode
} from '../../container/container-utils';
import type { Action } from '../../store/state/state-manager';
import CaseUtils from '../utils';

import { executeOpenWorkByHandleRequest } from './async-request';
import { getOpenWorkByHandleSemanticURL, handleWorkItemActivation } from './helper';
import { OPEN_WORK_BY_HANDLE } from './action-creator';

const { RESOURCE_TYPES } = publicConstants;

const getChildCaseActivationPayload = (target: string, insKey: string) => {
  const containerItemID = getContainerItemName(target, insKey);
  if (!containerItemID) {
    return null;
  }

  const existingItem = getContainerItemData(target, containerItemID);
  const isAssignmentWithContext =
    existingItem?.resourceType === RESOURCE_TYPES.ASSIGNMENT &&
    typeof existingItem?.context === 'string' &&
    existingItem.context !== '';

  if (!isAssignmentWithContext) {
    return null;
  }

  const contextData = StateManager.getDataObject(existingItem.context);
  const isChildCase = !!contextData?.caseInfo?.parentCaseInfo;

  const semanticURL = typeof existingItem.semanticURL === 'string' ? existingItem.semanticURL.trim() : '';

  if (!isChildCase || !semanticURL) {
    return null;
  }

  return {
    target,
    containerItemID,
    semanticURL,
    insKey
  };
};

const addActionsFoOpenWorkByHandle = async (chainedData: any, actionsOut: Action[], isTraditionalUICase: boolean) => {
  const { actionInPayload, activateContainerActionPayload, type, response, removeActionPayload, headers } = chainedData;
  const { context: actionInContext, skipBrowserSemanticUrlUpdate, debugInfo } = actionInPayload;
  const containerName = getContainerName(actionType.ACTION_OPENWORKBYHANDLE, actionInPayload);
  const target = getResolvedTargetName(containerName, actionInContext);

  /*
usecase: work item details are already available in redux store
*/
  if (isActivateContainerItemActionType(type)) {
    await handleWorkItemActivation(actionsOut, {
      activateContainerActionPayload,
      actionInPayload
    });
  } else {
    // since the work item is not available, get the context
    const context = getContext(target, containerName, actionInContext, { buildNew: true });
    const translatedDXAPIResponse = getTranslatedDXAPIResponse(response);
    const semanticURL = getOpenWorkByHandleSemanticURL(translatedDXAPIResponse, actionInPayload);

    translatedDXAPIResponse.semanticURL = semanticURL;
    translatedDXAPIResponse.directUrlUpdation = !skipBrowserSemanticUrlUpdate;

    /* [TODO]: we shouldn't use "acName" anywhere, setting this value..since app-router uses this logic */
    const options = { acName: target, context };

    await processViewResponse(translatedDXAPIResponse, actionInPayload, options);

    const payload = getActionPayload(translatedDXAPIResponse, {
      target,
      context,
      containerItemID: context,
      semanticURL,
      headers,
      debugInfo
    });

    // open work by handle should render in review mode
    payload.caseViewMode = constants.REVIEW_MODE;
    payload.resourceType = RESOURCE_TYPES.CASE;
    if (isTraditionalUICase) {
      payload.data.caseInfo.content.coexistenceAction = 'openWorkByHandle';
      payload.data.caseID = payload.data.caseInfo.ID;
    }
    CaseUtils.setTraditionalGadgetId(payload.data);
    updateKeyAndFlowName(payload, translatedDXAPIResponse, { skipFlowNameUpdate: true });

    payload.currentCaseViewMode = getCurrentCaseViewMode(chainedData?.actionInPayload?.context);

    const action = getActionBasedOnContainerMode(target, payload);
    if (removeActionPayload.containerItemID && isAddContainerAction(action)) {
      actionsOut.push(
        actions.postProcess(actions.removeContainerItem, {
          containerItemID: removeActionPayload.containerItemID,
          target
        })
      );
    }
    actionsOut.push(action);
  }
  addPreviewResetAction(actionsOut);
  const caseKey = actionInPayload.insKey;
  publishCaseOpenedEvent({ caseKey, actionInContext });
};

export const openWorkByHandle = (action$: Observable<Action>) => {
  let actionMgrID = 0;
  let actionsOut: any[] = [];

  return action$.pipe(
    ofType(OPEN_WORK_BY_HANDLE),
    map((action: Action) => {
      ({ actionMgrID } = action.payload);
      return action.payload;
    }),
    mergeMap((actionInPayload) => {
      const { context, isChild, viewType } = actionInPayload;

      const containerName = getContainerName(actionType.ACTION_OPENWORKBYHANDLE, actionInPayload);
      const target = getResolvedTargetName(containerName, context);

      let continueExecution = true;
      let removeActionPayload;
      if (viewType !== 'none') {
        const dirtyDetails = getDirtyDetails(target);

        continueExecution = dirtyDetails.continueExecution;
        removeActionPayload = dirtyDetails.removeActionPayload;
      }

      /*
        if user clicks on cancel on dirty dialog popup
          - return empty observable to stop the chaining process,
        else
          - continue the chaining process.
      */
      if (!continueExecution) {
        ActionManagerInstance.rejectActionByID(actionMgrID);
        return empty();
      }

      /*
        check whether work item is already available in redux store.
          - if already present activate the work item by disptaching ACTIVATE_CONTAINER_ITEM action
      */
      let { activate, activateContainerActionPayload } = getActivationDetails(
        RESOURCE_TYPES.CASE,
        target,
        actionInPayload
      );

      const isSingleDoc = isContainerSingleMode(target);
      if (!activate && !isSingleDoc && actionInPayload.insKey && target) {
        const childActivationPayload = getChildCaseActivationPayload(target, actionInPayload.insKey);
        if (childActivationPayload) {
          activate = true;
          activateContainerActionPayload = childActivationPayload;
        }
      }

      if (activate && !isChild && viewType !== 'none') {
        updateTraditionalGadgetId(activateContainerActionPayload);

        return of({
          type: actionType.ACTIVATE_CONTAINER_ITEM,
          activateContainerActionPayload,
          actionInPayload
        });
      }

      return executeOpenWorkByHandleRequest(actionInPayload, { removeActionPayload });
    }),
    mergeMap(async (chainedData) => {
      if (chainedData && isErrorActionType(chainedData.type)) {
        actionsOut.push(processDataToDispatchErrorAction(chainedData));
        return actionsOut;
      }
      const containerData = StateManager.getDataObject(chainedData?.actionInPayload?.context);
      const isTraditionalUICase = isCoexistenceCase(chainedData?.actionInPayload.className);
      if (chainedData?.actionInPayload?.viewType === 'none') {
        if (containerData?.context_data?.caseViewMode !== 'perform') {
          const translatedDXAPIResponse1 = getTranslatedDXAPIResponse(chainedData?.response);
          const currentPayload = {
            data: {
              ...translatedDXAPIResponse1?.data
            },
            context: chainedData?.actionInPayload?.context,
            isArrayDeepMerge: true
          };
          actionsOut.push({
            type: actionType.DELETE_CASE_SUMMARY,
            payload: {
              context: currentPayload.context,
              doDeleteConfirmationDetails: false
            }
          });
          if (isTraditionalUICase && currentPayload.data.caseInfo?.content) {
            currentPayload.data.caseInfo.content.coexistenceAction = 'openWorkByHandle';
            currentPayload.data.caseID = currentPayload.data.caseInfo.ID;
          }
          CaseUtils.setTraditionalGadgetId(currentPayload.data);
          actionsOut.push(actions.postProcess(actions.mergeData, currentPayload));
          const target = getTargetFromContainerItemID(chainedData?.actionInPayload?.context);
          const containerName = getContainerNameFromContainerItemID(chainedData?.actionInPayload?.context);

          const currentContainerInfo = Object.freeze({
            target,
            containerItemID: chainedData?.actionInPayload?.context,
            context: chainedData?.actionInPayload?.context,
            containerName
          });
          if (isParentAndChildContextHoldingSameItem(chainedData?.actionInPayload?.context)) {
            const payload = getActionPayload(chainedData?.response, {
              ...currentContainerInfo
            });
            addActionsToParentContext({ ...payload, isArrayDeepMerge: true }, actionsOut);
          }
        }
      } else {
        await addActionsFoOpenWorkByHandle(chainedData, actionsOut, isTraditionalUICase);
      }
      return actionsOut;
    }),
    map(() => {
      StateManager.dispatch(actionsOut);
      promiseHandler(actionsOut, actionMgrID);
      actionsOut = [];
      return actionsOut;
    })
  );
};

export default openWorkByHandle;
